import { ErrorCode, ERROR_SPECS } from "@sarva/domain-types";
import { Button, Icon } from "@sarva/ui";
import { Component, type ErrorInfo, type ReactNode } from "react";
import { getLastRequestId } from "@/shared/api";

interface Props {
  children: ReactNode;
  /**
   * Shown for support (doc 27 §6, 5xx row). Defaults to the `X-Request-Id` of
   * the most recent response, which is what correlates a failed render with the
   * call that produced the data it choked on — the boundary catches the render,
   * long after the response object is gone.
   */
  requestId?: string;
}

interface State {
  error: Error | null;
}

/**
 * (H) doc 27 §6: no raw exception text, stack trace or opaque code reaches a
 * user. The boundary shows the generated 500 message plus a reference, and a
 * way forward. The stack goes to the console, which is not the user's screen.
 */
export class AppErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Deliberately not `console.log` with the props: a props dump from this
    // application can contain PHI.
    console.error("Unhandled UI error", error.message, info.componentStack);
  }

  override render(): ReactNode {
    const { error } = this.state;
    if (error === null) return this.props.children;

    const spec = ERROR_SPECS[ErrorCode.INTERNAL_ERROR];
    const reference =
      this.props.requestId ?? getLastRequestId() ?? "not available";
    return (
      <div
        role="alert"
        className="display-type mx-auto mt-12 flex max-w-prose flex-col items-center gap-3 rounded-lg border border-default bg-default p-6 text-center shadow-card"
      >
        <Icon name="octagon" size={6} className="text-danger" />
        <h1 className="text-3xl text-default">{spec.title}</h1>
        <p className="text-sm text-secondary">
          {spec.detailTemplate.replace("{request_id}", reference)}
        </p>
        <div className="mt-2 flex gap-2">
          <Button
            variant="secondary"
            onClick={() => this.setState({ error: null })}
          >
            Try again
          </Button>
          <Button variant="primary" onClick={() => window.location.assign("/")}>
            Back to the dashboard
          </Button>
        </div>
      </div>
    );
  }
}
